import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Tipos simplificados para evitar stack overflow no TypeScript compiler
type ReportMetrics = {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  criticalTickets: number;
};

type ReportStatusData = { name: string; value: number; color: string };
type ReportTypeData = { name: string; total: number };
type ReportMonthlyData = {
  date: string;
  incidents: number;
  requests: number;
  problems: number;
  changes: number;
  total: number;
};

type ReportProductivityMetrics = {
  avgResolutionHours: number;
  resolutionRate: number;
  sameDay: number;
  oneToThreeDays: number;
  threeToSevenDays: number;
  moreThanSevenDays: number;
  backlogChange: number;
};

type ReportTeamPerformance = {
  name: string;
  totalTickets: number;
  resolved: number;
  resolutionRate: number;
  avgResolutionHours: number;
};

type ReportSLAMetrics = {
  categoria: string;
  total: number;
  cumpridos: number;
  descumpridos: number;
  taxa_cumprimento: number;
};

export interface ReportData {
  metrics: ReportMetrics;
  statusData: ReportStatusData[];
  typeData: ReportTypeData[];
  monthlyData: ReportMonthlyData[];
  productivityMetrics?: ReportProductivityMetrics;
  teamPerformance?: ReportTeamPerformance[];
  slaMetrics?: ReportSLAMetrics[];
  selectedMonth: Date;
}

/**
 * Exporta o relatório completo em PDF
 */
export const exportToPDF = async (elementId: string, fileName: string, selectedMonth: Date) => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Elemento não encontrado');
    }

    // Captura o elemento como imagem
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Adiciona cabeçalho
    pdf.setFontSize(16);
    pdf.text(`Relatório de Tickets - ${format(selectedMonth, "MMMM 'de' yyyy", { locale: ptBR })}`, 10, 10);
    position = 20;

    // Adiciona a imagem
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Adiciona páginas extras se necessário
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Adiciona rodapé
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.text(
        `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })} - Página ${i} de ${pageCount}`,
        10,
        290
      );
    }

    pdf.save(fileName);
  } catch (error) {
    console.error('Erro ao exportar PDF:', error);
    throw error;
  }
};

/**
 * Exporta os dados do relatório para Excel
 */
export const exportToExcel = (data: ReportData, fileName: string) => {
  try {
    const workbook = XLSX.utils.book_new();

    // Aba 1: Resumo Executivo
    const summaryData = [
      ['Relatório de Tickets'],
      ['Período', format(data.selectedMonth, "MMMM 'de' yyyy", { locale: ptBR })],
      ['Gerado em', format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })],
      [],
      ['Métricas Gerais'],
      ['Total de Tickets', data.metrics.totalTickets],
      ['Tickets Abertos', data.metrics.openTickets],
      ['Tickets Resolvidos', data.metrics.resolvedTickets],
      ['Tickets Críticos', data.metrics.criticalTickets],
    ];

    if (data.productivityMetrics) {
      summaryData.push(
        [],
        ['Métricas de Produtividade'],
        ['Tempo Médio de Resolução (horas)', data.productivityMetrics.avgResolutionHours.toFixed(2)],
        ['Taxa de Resolução (%)', data.productivityMetrics.resolutionRate.toFixed(2)],
        ['Resolvidos no Mesmo Dia', data.productivityMetrics.sameDay],
        ['Resolvidos em 1-3 Dias', data.productivityMetrics.oneToThreeDays],
        ['Resolvidos em 3-7 Dias', data.productivityMetrics.threeToSevenDays],
        ['Resolvidos em >7 Dias', data.productivityMetrics.moreThanSevenDays],
        ['Mudança no Backlog', data.productivityMetrics.backlogChange]
      );
    }

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    // Aba 2: Distribuição por Status
    const statusSheet = XLSX.utils.json_to_sheet(
      data.statusData.map(item => ({
        Status: item.name,
        Quantidade: item.value,
        Percentual: data.metrics.totalTickets > 0 
          ? `${((item.value / data.metrics.totalTickets) * 100).toFixed(1)}%` 
          : '0%',
      }))
    );
    XLSX.utils.book_append_sheet(workbook, statusSheet, 'Status');

    // Aba 3: Distribuição por Tipo
    const typeSheet = XLSX.utils.json_to_sheet(
      data.typeData.map(item => ({
        Tipo: item.name,
        Total: item.total,
        Percentual: data.metrics.totalTickets > 0 
          ? `${((item.total / data.metrics.totalTickets) * 100).toFixed(1)}%` 
          : '0%',
      }))
    );
    XLSX.utils.book_append_sheet(workbook, typeSheet, 'Tipos');

    // Aba 4: Evolução Diária
    const dailySheet = XLSX.utils.json_to_sheet(
      data.monthlyData.map(item => ({
        Data: item.date,
        Incidentes: item.incidents,
        Requisições: item.requests,
        Problemas: item.problems,
        Mudanças: item.changes,
        Total: item.total,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'Evolução Diária');

    // Aba 5: Performance de Equipes (se disponível)
    if (data.teamPerformance && data.teamPerformance.length > 0) {
      const teamSheet = XLSX.utils.json_to_sheet(
        data.teamPerformance.map(item => ({
          Atendente: item.name,
          'Total de Tickets': item.totalTickets,
          Resolvidos: item.resolved,
          'Taxa de Resolução (%)': item.resolutionRate.toFixed(2),
          'Tempo Médio (horas)': item.avgResolutionHours.toFixed(2),
        }))
      );
      XLSX.utils.book_append_sheet(workbook, teamSheet, 'Performance de Equipes');
    }

    // Aba 6: Performance de SLA (se disponível)
    if (data.slaMetrics && data.slaMetrics.length > 0) {
      const slaSheet = XLSX.utils.json_to_sheet(
        data.slaMetrics.map(item => ({
          Categoria: item.categoria,
          Total: item.total,
          Cumpridos: item.cumpridos,
          Descumpridos: item.descumpridos,
          'Taxa de Cumprimento (%)': item.taxa_cumprimento.toFixed(2),
        }))
      );
      XLSX.utils.book_append_sheet(workbook, slaSheet, 'SLA Performance');
    }

    // Salva o arquivo
    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    console.error('Erro ao exportar Excel:', error);
    throw error;
  }
};

/**
 * Exporta dados brutos em CSV
 */
export const exportToCSV = (data: any[], fileName: string, headers?: string[]) => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    if (headers) {
      XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
    }

    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Erro ao exportar CSV:', error);
    throw error;
  }
};
